import { Link } from "react-router-dom";
import {
  Smartphone, Share, PlusSquare, MoreVertical, Download, UserPlus,
  HardDrive, FileText, Wrench, ShieldCheck, ArrowRight, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Reveal from "@/components/site/Reveal";

const features = [
  { icon: HardDrive, t: "Tus equipos, ordenados", d: "Registra cada sillón, autoclave, rayos X o compresor con su marca, modelo, serie y box." },
  { icon: FileText, t: "Repositorio documental", d: "Facturas de compra, mantenciones, certificados y documentos normativos por equipo — listos para cualquier inspección de la Seremi." },
  { icon: Wrench, t: "Solicita mantención en 1 toque", d: "Preventiva o falla urgente: tu solicitud llega de inmediato a nuestro equipo técnico por WhatsApp." },
  { icon: ShieldCheck, t: "Seguro y privado", d: "Verificación por correo, sesión cifrada y cada clínica ve solo su propia información." },
];

const Step = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0FB4B7] text-white text-sm font-bold flex-shrink-0">{n}</span>
    <span className="text-sm text-[#374151] leading-relaxed pt-0.5">{children}</span>
  </li>
);

export default function AppPage() {
  return (
    <div data-testid="app-page" className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B1020]">
        <div className="absolute inset-0 odf-grid opacity-[0.10]" />
        <div className="absolute -top-24 right-1/4 w-[420px] h-[420px] rounded-full bg-[#0FB4B7]/20 blur-[120px]" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10 py-20 md:py-28 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#0FB4B7]/30 bg-[#0FB4B7]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#0FB4B7] mb-6">
              <Smartphone className="h-3.5 w-3.5" /> Ya disponible · iOS y Android
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white leading-tight max-w-3xl mx-auto">
              La app de tu clínica,<br />gratis en tu teléfono
            </h1>
            <p className="mt-6 text-lg text-white/60 max-w-2xl mx-auto">
              Administra tus equipos, guarda tu documentación normativa y solicita
              mantención con un toque. Sin App Store, sin esperas: se instala
              directo desde el navegador.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/cliente">
                <Button size="lg" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white h-14 px-8">
                  <UserPlus className="h-5 w-5 mr-2" /> Crear mi cuenta gratis
                </Button>
              </Link>
              <a href="#instalar">
                <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white hover:text-[#0B1020] h-14 px-8">
                  <Download className="h-5 w-5 mr-2" /> Cómo instalarla
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {features.map((f, i) => (
              <Reveal key={f.t} delay={i * 80}>
                <div className="h-full rounded-3xl border border-gray-100 bg-[#F8FAFC] p-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#0FB4B7]/10 flex items-center justify-center mb-4">
                    <f.icon className="h-6 w-6 text-[#0FB4B7]" />
                  </div>
                  <h3 className="font-heading font-bold text-[#111827] mb-2">{f.t}</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTALAR */}
      <section id="instalar" className="py-20 md:py-24 bg-[#F8FAFC] border-y border-gray-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-4">Instalación en 30 segundos</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#111827]">
              Elige tu app e instálala desde el navegador
            </h2>
            <p className="mt-3 text-[#4B5563]">
              Hay una app para clínicas y otra para técnicos Odontofix. No necesitas
              App Store ni Google Play: se agregan a tu pantalla de inicio y funcionan
              como cualquier aplicación.
            </p>
          </Reveal>

          {/* Elige tu app */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            <Reveal>
              <div className="h-full rounded-3xl bg-white border-2 border-[#0FB4B7]/40 p-7 shadow-sm flex flex-col">
                <span className="inline-flex self-start rounded-full bg-[#0FB4B7]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#0D999C] mb-3">
                  Para clínicas
                </span>
                <h3 className="font-heading text-xl font-bold text-[#111827]">App Clientes</h3>
                <p className="text-sm text-[#4B5563] mt-2 flex-1">
                  Tus equipos, documentos normativos, historial de mantenciones y
                  solicitudes de servicio en un toque.
                </p>
                <a href="/movil/cliente" className="mt-5 block">
                  <Button className="w-full h-12 bg-[#0FB4B7] hover:bg-[#0D999C] text-white font-semibold">
                    <Download className="h-4 w-4 mr-2" /> Abrir App Clientes
                  </Button>
                </a>
                <p className="mt-2 text-[11px] text-gray-400 text-center">odontofix.cl/movil/cliente</p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="h-full rounded-3xl bg-white border-2 border-[#384091]/30 p-7 shadow-sm flex flex-col">
                <span className="inline-flex self-start rounded-full bg-[#384091]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#384091] mb-3">
                  Para técnicos Odontofix
                </span>
                <h3 className="font-heading text-xl font-bold text-[#111827]">App Técnicos</h3>
                <p className="text-sm text-[#4B5563] mt-2 flex-1">
                  Informes de mantención preventiva y correctiva, inventario con
                  pistoleo y rendición de gastos en terreno.
                </p>
                <a href="/movil/tecnico" className="mt-5 block">
                  <Button className="w-full h-12 bg-[#384091] hover:bg-[#2D3375] text-white font-semibold">
                    <Download className="h-4 w-4 mr-2" /> Abrir App Técnicos
                  </Button>
                </a>
                <p className="mt-2 text-[11px] text-gray-400 text-center">odontofix.cl/movil/tecnico</p>
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Reveal>
              <div className="h-full rounded-3xl bg-white border border-gray-100 p-7 shadow-sm">
                <h3 className="font-heading font-bold text-[#111827] mb-4">iPhone / iPad (Safari)</h3>
                <ol className="space-y-3">
                  <Step n="1">Abre el enlace de tu app en <b>Safari</b>: <b>odontofix.cl/movil/cliente</b> (clínicas) o <b>odontofix.cl/movil/tecnico</b> (técnicos).</Step>
                  <Step n="2">Toca el botón <b>Compartir</b> <Share className="inline h-4 w-4 text-[#0FB4B7]" /> (abajo al centro).</Step>
                  <Step n="3">Elige <b>"Añadir a pantalla de inicio"</b> <PlusSquare className="inline h-4 w-4 text-[#0FB4B7]" /> y confirma.</Step>
                  <Step n="4">Abre tu app desde la pantalla de inicio e inicia sesión.</Step>
                </ol>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="h-full rounded-3xl bg-white border border-gray-100 p-7 shadow-sm">
                <h3 className="font-heading font-bold text-[#111827] mb-4">Android (Chrome)</h3>
                <ol className="space-y-3">
                  <Step n="1">Abre el enlace de tu app en <b>Chrome</b>: <b>odontofix.cl/movil/cliente</b> (clínicas) o <b>odontofix.cl/movil/tecnico</b> (técnicos).</Step>
                  <Step n="2">Toca el menú <MoreVertical className="inline h-4 w-4 text-[#0FB4B7]" /> (arriba a la derecha) o el aviso <b>"Instalar app"</b>.</Step>
                  <Step n="3">Elige <b>"Instalar aplicación"</b> y confirma.</Step>
                  <Step n="4">Abre tu app desde la pantalla de inicio e inicia sesión.</Step>
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* REGISTRO */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <Reveal>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#111827] leading-tight mb-5">
                Regístrate y arma tu clínica en minutos
              </h2>
              <ol className="space-y-4">
                <Step n="1"><b>Crea tu cuenta</b> con tu correo — te enviamos un código de verificación.</Step>
                <Step n="2"><b>Tu clínica se crea una sola vez con su RUT</b> — todos los usuarios que se registren con ese RUT comparten los mismos equipos y documentos, cada uno con su propia sesión.</Step>
                <Step n="3"><b>Agrega tus equipos</b> (sillones, autoclave, rayos X…) con marca, modelo y serie.</Step>
                <Step n="4"><b>Sube tus documentos</b>: facturas, mantenciones y certificados quedan guardados y comprimidos.</Step>
                <Step n="5">¿Falla o mantención? <b>Solicítala con un toque</b> — nos llega al instante.</Step>
              </ol>
              <Link to="/cliente">
                <Button className="mt-7 bg-[#384091] hover:bg-[#2D3375] text-white h-12 px-6">
                  Comenzar ahora <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-3xl bg-gradient-to-br from-[#384091] to-[#0FB4B7] p-8 text-white">
                <h3 className="font-heading text-xl font-bold mb-5">¿Por qué les sirve a las clínicas?</h3>
                <ul className="space-y-3">
                  {["Todo el respaldo documental para inspecciones de la Seremi",
                    "Historial de mantenciones por equipo, siempre a mano",
                    "Solicitudes de servicio sin llamadas ni correos",
                    "Gratis para clientes Odontofix"].map((x) => (
                    <li key={x} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span className="text-sm leading-relaxed">{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
